import React, { useEffect, useState } from "react";
import { Pagination } from "../lib/api";

type PaginationProps = {
  pagination: Pagination;
  onPageChange: (page: number) => void;
}

export default function PaginationView({ pagination, onPageChange }: PaginationProps) {
  const currentPage = pagination.current_page || 1;
  const totalPages = pagination.total_pages || 1;
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const maxVisiblePages = 10;

  const createVisiblePages = (): (number | "...")[] => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let start = Math.max(1, currentPage - Math.floor((maxVisiblePages - 1) / 2));
    let end = start + maxVisiblePages - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    const pages: (number | "...")[] = [];

    if (start > 1) {
      pages.push(1);
      if (start > 2) {
        pages.push("...");
      }
    }

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push("...");
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const visiblePages = createVisiblePages();

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  const submitPageInput = () => {
    const page = Number(pageInput);
    if (!Number.isFinite(page) || page < 1 || page > totalPages) {
      setPageInput(String(currentPage));
      return;
    }
    goToPage(page);
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-slate-400 sm:text-sm">
          صفحه {currentPage} از {totalPages}
        </p>
        <form
          className="flex items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault();
            submitPageInput();
          }}
        >
          <input
            type="number"
            min={1}
            max={totalPages}
            value={pageInput}
            onChange={(event) => setPageInput(event.target.value)}
            className="h-8 w-16 rounded-lg border border-slate-700 bg-slate-950 px-2 text-center text-xs text-slate-100 outline-none transition focus:border-indigo-400 sm:text-sm"
            aria-label="شماره صفحه"
          />
          <button
            type="submit"
            className="rounded-lg border border-slate-700 px-2 py-1.5 text-xs text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300 sm:text-sm"
          >
            برو
          </button>
        </form>
      </div>

      <div className="flex items-center justify-center gap-1 sm:gap-2">
        <button
          type="button"
          disabled={!hasPrev}
          onClick={() => goToPage(currentPage - 1)}
          className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
        >
          قبلی
        </button>

        <div className="flex max-w-full items-center gap-1 overflow-x-auto sm:gap-2">
          {visiblePages.map((item, index) =>
            item === "..." ? (
              <span key={`ellipsis-${index}`} className="shrink-0 px-1 text-slate-400">
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => goToPage(item)}
                className={`h-7 min-w-7 shrink-0 rounded-lg px-1.5 text-xs transition sm:h-8 sm:min-w-8 sm:px-2 sm:text-sm ${
                  item === currentPage
                    ? "bg-indigo-500 text-white"
                    : "border border-slate-700 text-slate-200 hover:border-indigo-400 hover:text-indigo-300"
                }`}
              >
                {item}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          disabled={!hasNext}
          onClick={() => goToPage(currentPage + 1)}
          className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
        >
          بعدی
        </button>
      </div>
    </div>
  );
}